import { ErrorLogComponent } from '@cian/error-log-component';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Provider } from 'react-redux';

/***** CIAN LIBS *****/
import { DynamicCalltrackingContext } from '@cian/frontend-dynamic-calltracking-component';

import { initNewbuildingSearch } from '../../utils/initer/initer';

/***** CONTAINERS *****/
import { FiltersContainer } from '../FiltersContainer';
import { SeoLinksContainer } from '../SeoLinksContainer';
import { FilterTags } from '../FilterTags';
import { OffersContainer } from '../OffersContainer';
import { QuickLinksContainer } from '../QuickLinksContainer';
import { MarkedListContainer } from '../MarkedListContainer';
import { NewbuildingChatContainerLoadable } from '../NewbuildingChat';
import { ConsultantPopupContainerWrapper } from '../ConsultantPopup';

/***** COMPONENTS *****/
import { Container } from '../../components/Container/';
import { Layout } from '../../components/Layout/';
/***** CONSTANTS *****/
import { SIZE_12, SIZE_16 } from '../../constants/sizes';

/***** STYLES *****/
import * as styles from './App.css';

/***** TYPES *****/
import { IApplicationState, TReduxStore } from '../../types/redux';
import { TTypedThunkDispatch } from '../../types/thunk';
import { initJsonQuery } from '../../actions/filters';
import { setTags } from '../../actions/filterTags';
import { getOffersData } from '../../actions/offersData';
import { setMedia } from '../../actions/media';
import { debounce } from '@cian/newbuilding-utils';
import { DEFAULT_RESIZE_TIMEOUT } from '../../constants/timeouts';
import { setCurrentRegion } from '../../actions/region/currentRegion';
import { triggerHeaderRegionChanged } from '../../utils/events/triggerHeaderRegionChanged';
import { trackingActionShowSimilarJK } from '../../actions/analytics';
import { displayConsultantPhone } from '../../utils/displayNewbuildingConsultantPhone';
import { uaToDeviceType } from '@cian/ui-kit/utils';
import { UiKitContextProvider } from '@cian/ui-kit/context';
import { ICustomContextParams } from '../../types/ICustomContextParams';
import { IApplicationContext } from '../../types/applicationContext';

export interface IApplicationProps extends IApplicationContext {
  reduxStore: TReduxStore;
  customContext: ICustomContextParams;
}

export type TApplicationContextTypes = {
  [K in keyof IApplicationContext]: PropTypes.Requireable<IApplicationContext[K]>;
};

/***** COMPONENT *****/
export class Application extends React.Component<IApplicationProps> {
  // TODO: переделать на React Context API
  public static childContextTypes: TApplicationContextTypes = {
    // tslint:disable-next-line:no-any
    config: PropTypes.object as any,
    // tslint:disable-next-line:no-any
    logger: PropTypes.object as any,
    // tslint:disable-next-line:no-any
    httpApi: PropTypes.object as any,
  };

  public constructor(props: IApplicationProps) {
    super(props);
  }

  // TODO: переделать на React Context API
  public getChildContext(): IApplicationContext {
    return {
      config: this.props.config,
      logger: this.props.logger,
      httpApi: this.props.httpApi,
    };
  }

  public componentDidMount() {
    initNewbuildingSearch({
      logger: this.props.logger,
      regionId: this.props.reduxStore.getState().region.currentRegion.id,
      store: this.props.reduxStore,
      subdomain: this.props.customContext.subdomain,
      httpApi: this.props.httpApi,
    });
    const dispatch = this.props.reduxStore.dispatch as TTypedThunkDispatch;
    const storeState = this.props.reduxStore.getState();
    const similarNewbuildings =
      storeState.offersData.similarNewbuildings && storeState.offersData.similarNewbuildings.length;
    const canUseHiddenBase = storeState.user.isAuthenticated && storeState.user.permissions.canUseHiddenBase;
    const consultantPhone = this.props.config.get<string>('newbuildingConsultant.phone.moscowAndRegion');
    const consultantPhoneEnabledRegions =
      this.props.config.get<number[]>('newbuildingConsultant.phone.enabledRegions') || [];

    displayConsultantPhone(storeState, consultantPhoneEnabledRegions, consultantPhone);
    this.initPushState(storeState);
    if (!storeState.offersData.newbuildings.length && !similarNewbuildings) {
      dispatch(getOffersData({ shouldUpdateHistory: true, canUseHiddenBase }));
    }
    if (similarNewbuildings) {
      dispatch(trackingActionShowSimilarJK(storeState.jsonQuery));
    }

    window.addEventListener('popstate', this.handlePopstate(dispatch), false);
    this.handleResize();
    window.addEventListener('resize', this.handleResize, false);
  }

  public render() {
    const { logger, httpApi, config, customContext } = this.props;

    return (
      <ErrorLogComponent logger={logger}>
        <UiKitContextProvider deviceType={uaToDeviceType(customContext.userAgent)}>
          <DynamicCalltrackingContext.Provider value={{ httpApi, logger }}>
            <Provider store={this.props.reduxStore}>
              <Container display="flex" flexDirection="column-reverse" printAsBlock position="relative">
                <Container dataMark="SeoLinksContainer" backgroundColor="gray_0" padding={`0 0 ${SIZE_16} 0`} noPrint>
                  <Layout>
                    <MarkedListContainer />
                    <SeoLinksContainer />
                  </Layout>
                </Container>
                <div>
                  <div className={styles[`fake_header`]} />
                  <Container
                    borderColor="gray_1"
                    borderTopWidth={0}
                    borderBottomWidth={0}
                    borderRightWidth={0}
                    borderLeftWidth={0}
                    borderStyle="solid"
                    padding={`${SIZE_16} 0`}
                    noPrint
                  >
                    <Layout>
                      <FiltersContainer />
                      <FilterTags />
                    </Layout>
                  </Container>
                  <div className={styles[`shadow`]} />
                  <Container backgroundColor="gray_0" padding={`0 0 ${SIZE_12}`}>
                    <Layout>
                      <QuickLinksContainer />
                      <OffersContainer />
                    </Layout>
                  </Container>
                </div>
                <NewbuildingChatContainerLoadable config={config} />
                <ConsultantPopupContainerWrapper />
              </Container>
            </Provider>
          </DynamicCalltrackingContext.Provider>
        </UiKitContextProvider>
      </ErrorLogComponent>
    );
  }

  private initPushState = (state: IApplicationState) => {
    const {
      jsonQuery,
      region: { currentRegion },
      location: { path, page },
      offersData: { seo },
      filters: { tags },
    } = state;
    const title = (seo && seo.title) || document.title || '';

    triggerHeaderRegionChanged(currentRegion);
    window.history.pushState(
      {
        jsonQuery,
        currentRegion,
        path,
        page,
        title,
        tags,
      },
      title,
      path,
    );
  }

  private handleResize = debounce(DEFAULT_RESIZE_TIMEOUT, () => {
    const dispatch = this.props.reduxStore.dispatch;

    dispatch(setMedia());
  });

  private handlePopstate = (dispatch: TTypedThunkDispatch) => ({ state }: PopStateEvent) => {
    if (state && state.jsonQuery && state.path && state.page) {
      const storeState = this.props.reduxStore.getState();
      const canUseHiddenBase = storeState.user.isAuthenticated && storeState.user.permissions.canUseHiddenBase;

      document.title = state.title || document.title;
      dispatch(initJsonQuery(state.jsonQuery));
      dispatch(setCurrentRegion(state.currentRegion));
      dispatch(setTags(state.tags || []));
      dispatch(
        getOffersData({
          shouldUpdateHistory: false,
          path: state.path,
          page: state.page,
          canUseHiddenBase,
        }),
      );
    }
  }
}
